import { Bar, getElementAtEvent } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartEvent,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { assetTypesColors } from "@features/assets-feature/utils/assetsColors";
import { formatShortNumber } from "../AssetsSpendWidget";
import { Box, Skeleton, useTheme } from "@mui/material";
import { useApi } from "@utils/useApi";
import {
  GetPurchasedAssetsSpendWidgetRequest,
  ManufacturerSpendDataDto,
  PurchasedAssetsSpendWidgetClient,
} from "@lib/ShiOneClient";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { buildPurchasedAssetsSpendDeepLink } from "./spendWidgetDeepLink";
const TOP_MANUFACTURERS_COUNT = 7;

function sortByMonth(
  data: ManufacturerSpendDataDto[],
): ManufacturerSpendDataDto[] {
  return [...data].sort((a, b) => {
    const aYear = a.year ?? 0;
    const bYear = b.year ?? 0;
    if (aYear !== bYear) return aYear - bYear;
    const aMonth = a.month ?? 0;
    const bMonth = b.month ?? 0;
    return aMonth - bMonth;
  });
}

function filterLastNMonths(
  data: ManufacturerSpendDataDto[],
  termMonths: number,
): ManufacturerSpendDataDto[] {
  if (data.length === 0) return [];
  const mostRecent = data.at(-1);
  const mostRecentYear = mostRecent?.year ?? 0;
  const mostRecentMonth = mostRecent?.month ?? 0;
  return data.filter((entry) => {
    if (entry.year == null || entry.month == null) return false;
    const monthsDiff =
      (mostRecentYear - entry.year) * 12 + (mostRecentMonth - entry.month);
    return monthsDiff >= 0 && monthsDiff < termMonths;
  });
}

function aggregateByManufacturer(
  data: ManufacturerSpendDataDto[],
): Record<string, number> {
  return data.reduce(
    (acc, curr) => {
      if (!curr.manufacturer || typeof curr.amount !== "number") return acc;
      if (!acc[curr.manufacturer]) {
        acc[curr.manufacturer] = 0;
      }
      acc[curr.manufacturer] =
        Math.round((acc[curr.manufacturer] + curr.amount) * 100) / 100;
      return acc;
    },
    {} as Record<string, number>,
  );
}

type ManufacturerSpend = { manufacturer: string; amount: number };

function sortByAmount(aggregated: Record<string, number>): ManufacturerSpend[] {
  return Object.entries(aggregated)
    .sort((a, b) => b[1] - a[1])
    .map(([manufacturer, amount]) => ({ manufacturer, amount }));
}

function addOther(
  data: ManufacturerSpend[],
  topCount: number,
): ManufacturerSpend[] {
  if (data.length <= topCount) {
    return data;
  }
  const top = data.slice(0, topCount);
  const otherSum = data
    .slice(topCount)
    .reduce((sum, entry) => sum + entry.amount, 0);
  return [...top, { manufacturer: "Other", amount: otherSum }];
}

const useGetSpendByManufacturer = () => {
  const request = {
    clientLocalNow: DateTime.local().toISO(),
  } as unknown as GetPurchasedAssetsSpendWidgetRequest;
  const api = useApi(PurchasedAssetsSpendWidgetClient);

  return useQuery({
    queryKey: ["purchased-assets-spend-widget", "by-manufacturer"],
    queryFn: () => api.getWidgetSpendByManufacturer(request),
  });
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const getChartOptions = (
  theme: any,
  getManufacturerByIndex: (index: number) => string | undefined,
) => ({
  indexAxis: "y" as const,
  maintainAspectRatio: false,
  onHover: (event: ChartEvent, _elements: unknown, chart: ChartJS<"bar">) => {
    const isInsidePlotArea =
      typeof event.x === "number" &&
      typeof event.y === "number" &&
      event.x >= chart.chartArea.left &&
      event.x <= chart.chartArea.right &&
      event.y >= chart.chartArea.top &&
      event.y <= chart.chartArea.bottom;

    const hoveredElements = isInsidePlotArea
      ? chart.getElementsAtEventForMode(
          event.native as Event,
          "nearest",
          { intersect: true },
          false,
        )
      : [];

    const hoveredManufacturer = hoveredElements[0]
      ? getManufacturerByIndex(hoveredElements[0].index)
      : undefined;

    chart.canvas.style.cursor =
      hoveredManufacturer && hoveredManufacturer !== "Other" ? "pointer" : "default";
  },
  scales: {
    x: {
      min: 0,
      ticks: {
        display: true,
        color: theme.palette.text.secondary,
        callback: function (value: number | string) {
          const num =
            typeof value === "string" ? Number.parseFloat(value) : value;
          return `$${formatShortNumber(num)}`;
        },
      },
      grid: {
        drawTicks: false,
      },
    },
    y: {
      ticks: {
        display: true,
        color: theme.palette.text.secondary,
        padding: 10,
      },
      grid: {
        drawTicks: false,
      },
    },
  },
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  parsing: {
    xAxisKey: "key",
    yAxisKey: "value",
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: false,
    },
    datalabels: {
      anchor: "end" as const,
      align: "right" as const,
      formatter: (value: number) => {
        return `$${value.toLocaleString("en-US")}`;
      },
      color: theme.palette.text.secondary,
      font: {
        weight: "normal" as const,
      },
    },
  },
});

const AssetsSpendByManufacturer = ({ termMonths }: { termMonths: number }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const chartRef = useRef<ChartJS<"bar">>(null);
  const { data: response, isLoading } = useGetSpendByManufacturer();
  const rawSpendData = response?.data?.manufacturerSpendData || [];
  const sortedSpendData = sortByMonth(rawSpendData);
  const clippedSpend = filterLastNMonths(sortedSpendData, termMonths);
  const aggregatedSpend = aggregateByManufacturer(clippedSpend);
  const sortedAggSpend = sortByAmount(aggregatedSpend);
  const manufacturerGroups = addOther(sortedAggSpend, TOP_MANUFACTURERS_COUNT);

  const handleManufacturerLabelClick = (manufacturer: string) => {
    if (!manufacturer || manufacturer === "Other") {
      return;
    }

    const deepLink = buildPurchasedAssetsSpendDeepLink({
      viewBy: "manufacturer",
      termMonths,
      manufacturer,
    });

    navigate(deepLink);
  };

  const chartData = {
    labels: manufacturerGroups.map((item) => item.manufacturer),
    datasets: [
      {
        label: "Spend",
        data: manufacturerGroups.map((item) => item.amount),
        borderColor: assetTypesColors.hardware,
        backgroundColor: assetTypesColors.hardware,
        fill: false,
        barPercentage: 1,
        categoryPercentage: 0.67,
        barThickness: 20,
        minBarLength: 4,
      },
    ],
  };

  function widgetContents() {
    if (isLoading) {
      return <Skeleton variant="rectangular" width="100%" height={300} />;
    }
    if (sortedAggSpend.length) {
      const numBars = chartData.labels.length;
      const barThickness = 20;
      const categoryPercentage = 0.67;
      const rowHeight = Math.ceil(barThickness / categoryPercentage);
      const xAxisHeight = 40;
      const canvasHeight = Math.max(numBars * rowHeight + xAxisHeight, 80);

      const maxValue = Math.max(...chartData.datasets[0].data);
      const xMax = Math.ceil(maxValue * 1.3);
      const baseOptions = getChartOptions(
        theme,
        (index: number) => manufacturerGroups[index]?.manufacturer,
      );

      const options = {
        ...baseOptions,
        scales: {
          ...baseOptions.scales,
          x: {
            ...baseOptions.scales.x,
            max: xMax,
          },
        },
      };
      return (
        <Box
          display="flex"
          sx={{ p: 2, pb: 2, width: "100%" }}
          flexDirection="column"
        >
          <Box sx={{ fontWeight: "bold", mb: 1 }}>Spend by Manufacturer</Box>
          <Box sx={{ height: canvasHeight, position: "relative" }}>
            <Bar
              ref={chartRef}
              data={chartData}
              options={options}
              plugins={[ChartDataLabels]}
              onClick={(event) => {
                if (!chartRef.current) {
                  return;
                }

                const clickedElements = getElementAtEvent(chartRef.current, event);
                if (!clickedElements.length) {
                  return;
                }

                const selectedManufacturer =
                  manufacturerGroups[clickedElements[0].index]?.manufacturer;
                if (selectedManufacturer) {
                  handleManufacturerLabelClick(selectedManufacturer);
                }
              }}
            />
          </Box>
        </Box>
      );
    }
    return (
      <Box display="flex" sx={{ p: 2, justifyContent: "space-between" }}>
        <Box sx={{ fontWeight: 500 }}>Spend by Manufacturer</Box>
      </Box>
    );
  }

  return <>{widgetContents()}</>;
};

export default AssetsSpendByManufacturer;
